import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Faceted,
  FacetedBadgeList,
  FacetedContent,
  FacetedEmpty,
  FacetedGroup,
  FacetedInput,
  FacetedItem,
  FacetedList,
  FacetedSeparator,
  FacetedTrigger,
} from '@/components/ui/faceted';
import { mapSuppliersToOptions } from '@/hooks/supplier/map-suppliers-to-options';
import { useGetPurchasePlanningSuppliers } from '@/services/apis/queries/purchase-planning/use-get-purchase-planning-suppliers';
import type { Option } from '@/types/data-table';
import { usePurchasePlanningFilters } from './hooks/use-purchase-planning-filters';
import { usePurchasePlanningWeek } from './hooks/use-purchase-planning-week';

export function PurchasePlanningSupplierFilter() {
  const { t } = useTranslation(['purchasePlanning', 'table']);
  const { filters, setFilters, supplierIds, setSelectedSupplierIds } =
    usePurchasePlanningFilters();
  const { weekParams } = usePurchasePlanningWeek();
  const { data: suppliers } = useGetPurchasePlanningSuppliers(weekParams);
  const [prioritizedSupplierIds, setPrioritizedSupplierIds] = useState<
    string[]
  >([]);

  const supplierOptions = useMemo<Option[]>(
    () => mapSuppliersToOptions(suppliers),
    [suppliers],
  );

  const orderedSupplierOptions = useMemo(() => {
    const optionsByValue = new Map(
      supplierOptions.map((option) => [option.value, option]),
    );
    const selectedOptions = prioritizedSupplierIds.flatMap((supplierId) => {
      const option = optionsByValue.get(supplierId);
      return option ? [option] : [];
    });
    const prioritizedSupplierIdSet = new Set(prioritizedSupplierIds);
    const unselectedOptions = supplierOptions.filter(
      (option) => !prioritizedSupplierIdSet.has(option.value),
    );

    return [...selectedOptions, ...unselectedOptions];
  }, [prioritizedSupplierIds, supplierOptions]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setPrioritizedSupplierIds(supplierIds);
      }
    },
    [supplierIds],
  );

  useEffect(() => {
    const availableSupplierIds = new Set(
      supplierOptions.map((option) => option.value),
    );
    const availableSelectedSupplierIds = supplierIds.filter((supplierId) =>
      availableSupplierIds.has(supplierId),
    );

    if (availableSelectedSupplierIds.length !== supplierIds.length) {
      setSelectedSupplierIds(availableSelectedSupplierIds);
    }
  }, [supplierIds, supplierOptions, setSelectedSupplierIds]);

  useEffect(() => {
    const supplierFilter = filters.find((filter) => filter.id === 'supplierId');
    const supplierFilterValues = Array.isArray(supplierFilter?.value)
      ? supplierFilter.value
      : [];

    if (supplierFilterValues.length === 0) {
      return;
    }

    setSelectedSupplierIds(
      supplierIds.length > 0 ? supplierIds : supplierFilterValues,
    );
    setFilters((currentFilters) =>
      currentFilters.filter((filter) => filter.id !== 'supplierId'),
    );
  }, [filters, setFilters, setSelectedSupplierIds, supplierIds]);

  return (
    <Faceted
      multiple
      onOpenChange={handleOpenChange}
      onValueChange={setSelectedSupplierIds}
      value={supplierIds}
    >
      <FacetedTrigger asChild>
        <Button className='h-9 min-w-[240px] justify-start' variant='outline'>
          <FacetedBadgeList
            badgeClassName='max-w-[180px] truncate'
            max={1}
            options={supplierOptions}
            placeholder={t('selectOneOrMoreSuppliers')}
          />
        </Button>
      </FacetedTrigger>
      <FacetedContent className='w-xs'>
        <FacetedInput placeholder={t('search options', { ns: 'table' })} />
        <FacetedList className='max-h-full'>
          <FacetedEmpty>{t('no options found', { ns: 'table' })}</FacetedEmpty>
          <FacetedGroup className='max-h-[300px] overflow-y-auto overflow-x-hidden'>
            {orderedSupplierOptions.map((option) => (
              <FacetedItem
                disabled={
                  supplierIds.length >= 3 && !supplierIds.includes(option.value)
                }
                key={option.value}
                value={option.value}
              >
                <span className='truncate'>{option.label}</span>
              </FacetedItem>
            ))}
          </FacetedGroup>
          {supplierIds.length > 0 && (
            <>
              <FacetedSeparator />
              <FacetedGroup>
                <FacetedItem
                  onSelect={() => setSelectedSupplierIds([])}
                  value='__clear__'
                >
                  <span className='w-full text-center'>
                    {t('clear selection', { ns: 'table' })}
                  </span>
                </FacetedItem>
              </FacetedGroup>
            </>
          )}
        </FacetedList>
      </FacetedContent>
    </Faceted>
  );
}
